import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import AppLayout from '@/layouts/AppLayout'
import { Icon } from '@/icons/Icon'
import { get } from '@/api/client'
import { HiddenInPublicView, useViewMode } from '@/lib/viewMode'
import CircularCard, { useViewerState } from '@/components/CircularCard'
import { OneCircularOpen } from '@/lib/expandedCircular'
import { BrandLogo, CONNECTION_LOGOS } from '@/components/BrandLogo'
import emailSvg from '@/assets/icons/brand/email.svg'
import slackSvg from '@/assets/icons/brand/slack.svg'
import teamsSvg from '@/assets/icons/brand/microsoft-teams.svg'
import jiraSvg from '@/assets/icons/brand/jira.svg'
import excelSvg from '@/assets/icons/brand/excel.svg'
import type { CircularDetail } from '@/types/circulars'

import '@/styles/pages/dashboard.css'
import '@/styles/pages/dashboard-due.css'
import '@/styles/pages/dashboard-attention.css'

/* ------------------------------------------------------------------ *
 * Response shape of GET /dashboard
 * ------------------------------------------------------------------ */

interface BannerTask {
  id: string
  changeId: string
  icon: string
  ownerDepartment: string
  ownerName: string
  severityAccent: string
  title: string
  dueLabel: string | null
  dueOn: string | null
  dueTone: string | null
}

interface Metric {
  key: string
  label: string
  period: string
  value: string
  valueDisplay: string
  valueFormat: string
  delta: string | null
  deltaDirection: 'up' | 'down' | null
  foot: string | null
  accent: string
  icon: string
  ordinal: number
}

interface BriefingSummary {
  id: string
  name: string
  frequencyLabel: string
  icon: string
  status: string
}

interface Portfolio {
  premiumDisplay: string
  policyCount: number
  sourceLabel: string
  badge: string
  note: string
  hasData: boolean
}

interface ConnectionSummary {
  id: string
  name: string
  icon: string
  status: string
  kind?: string
}

/** The channel's own logo where there is one; the line icon otherwise. */
const CHANNEL_LOGOS: Record<string, string> = {
  email: emailSvg,
  slack: slackSvg,
  teams: teamsSvg,
  jira: jiraSvg,
  excel: excelSvg,
}

interface DashboardResponse {
  bannerHeadline: string
  bannerDeadlinePill: string | null
  bannerContextText: string | null
  bannerTasks: BannerTask[]
  metrics: Metric[]
  circulars: CircularDetail[]
  briefings: BriefingSummary[]
  portfolio: Portfolio | null
  connections: ConnectionSummary[]
}

/** accent (from the API) → the `.ix-icon-tile--*` modifier the prototype pairs it with. */
const TILE_MODIFIER: Record<string, string> = {
  blue: 'brand',
  green: 'success',
  violet: 'info',
  amber: 'warning',
}

/** Names the icon registry does not carry, mapped to the nearest glyph it does. */
const ICON_FALLBACK: Record<string, string> = {
  database: 'cloud',
}

const icon = (name: string) => ICON_FALLBACK[name] ?? name

export default function DashboardPage() {
  const navigate = useNavigate()
  const { isPublic } = useViewMode()
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => get<DashboardResponse>('/dashboard'),
    // The figures are counted on every request, so the front page keeps the
    // same cadence Analytics does. Left at the default the two screens could
    // legitimately disagree by half a minute, which reads as one being stale.
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  })

  return (
    <AppLayout title="Dashboard">
      {isPending && <DashboardSkeleton />}

      {isError && (
        <div className="ix-alert ix-alert--error" role="alert">
          <Icon name="alert-triangle" size={18} />
          <div>
            <span className="ix-alert__title">We could not load your dashboard</span>
            <span>{error instanceof Error ? error.message : 'Please try again.'}</span>
          </div>
        </div>
      )}

      {data && (
        <>
          {/* Action needed banner */}
          <section className="ix-banner">
            <div className="ix-banner__header">
              <div className="ix-banner__heading">
                <span className="ix-icon-tile ix-icon-tile--sm ix-icon-tile--solid">
                  <Icon name="bell" size={14} />
                </span>
                <span>{data.bannerHeadline}</span>
              </div>
              {data.bannerDeadlinePill && (
                <span className="ix-pill ix-pill--warning">{data.bannerDeadlinePill}</span>
              )}
            </div>

            {data.bannerContextText && (
              <div className="ix-banner__context">
                <div className="ix-banner__context-text">
                  <Icon name="mail" size={14} />
                  <span>{data.bannerContextText}</span>
                </div>
                <Link className="ix-link-action" to="/alerts">
                  View alerts →
                </Link>
              </div>
            )}

            {/* Your own open steps, and only yours: this list is what you can
                act on. It scrolls past four rather than pushing the dashboard
                down. Everyone else's work is on the board. */}
            {data.bannerTasks.length === 0 && (
              <div className="dash-attn__none">
                No step on any board is assigned to you right now.{' '}
                <Link to="/change-response">Open the response board</Link> to see what your teams are
                working on.
              </div>
            )}
            <div className={data.bannerTasks.length > 4 ? 'dash-attn dash-attn--scrolls' : 'dash-attn'}>
            {data.bannerTasks.map((task) => (
              <div className="ix-banner__task" key={task.id}>
                <div className="ix-banner__task-main">
                  <span className={`ix-icon-tile ix-icon-tile--${task.severityAccent}`}>
                    <Icon name={icon(task.icon)} size={16} />
                  </span>
                  <div className="ix-banner__task-text">
                    <div className={`ix-banner__task-owner ix-banner__task-owner--${task.severityAccent}`}>
                      {task.ownerDepartment} · {task.ownerName}
                    </div>
                    <div className="ix-banner__task-title">{task.title}</div>
                  </div>
                </div>
                <div className="ix-banner__task-side">
                  {/* The countdown, with the date it counts to in the tooltip.
                      This read "before Sep 1" - a date is the answer to "when",
                      and somebody scanning their own queue is asking "how long
                      have I got", which they then had to work out from a date
                      against today, row by row.

                      Omitted, not emptied: a step whose change states no
                      effective date and has no filing deadline has no deadline
                      to show. */}
                  {task.dueLabel && (
                    <span
                      className={`ix-banner__task-due ix-banner__task-due--${task.dueTone ?? 'later'}`}
                      title={task.dueOn ?? undefined}
                    >
                      {task.dueLabel}
                    </span>
                  )}
                  <button
                    className="ix-btn ix-btn--compact"
                    type="button"
                    onClick={() => navigate(`/change-response/${task.changeId}`)}
                  >
                    Open task →
                  </button>
                </div>
              </div>
            ))}
            </div>
          </section>

          {/* Metrics */}
          <section className="ix-grid ix-grid--5">
            {data.metrics.map((metric) => (
              <article className={`ix-metric ix-accent-${metric.accent}`} key={metric.key}>
                <div className="ix-metric__top">
                  <div className="ix-metric__labels">
                    <div className="ix-metric__label">{metric.label}</div>
                    <div className="ix-metric__period">{metric.period}</div>
                  </div>
                  <span
                    className={`ix-icon-tile ix-icon-tile--${TILE_MODIFIER[metric.accent] ?? 'brand'} ix-accent-tile`}
                  >
                    <Icon name={icon(metric.icon)} size={16} />
                  </span>
                </div>
                <div className="ix-metric__value-row">
                  <div className="ix-metric__value ix-accent-value">{metric.valueDisplay}</div>
                  {metric.delta && (
                    <span
                      className={
                        metric.deltaDirection === 'down'
                          ? 'ix-chip ix-chip--error'
                          : 'ix-chip ix-chip--success'
                      }
                    >
                      {metric.delta}
                    </span>
                  )}
                </div>
                {metric.foot && <div className="ix-metric__foot">{metric.foot}</div>}
              </article>
            ))}
          </section>

          {/* Feed + side rail */}
          <div className="dash-cols">
            <section>
              <div
                className="ix-card__body"
                style={{
                  gap: 'var(--ix-space-base)',
                  padding: 0,
                  background: 'none',
                  boxShadow: 'none',
                  border: 0,
                }}
              >
                <div className="ix-between">
                  <h2 className="ix-card-title">Recent Circulars</h2>
                  <Link className="ix-link-action" to="/circular-feed">
                    View all →
                  </Link>
                </div>

                <RecentCirculars circulars={data.circulars} />
              </div>
            </section>

            <div className="dash-side">
              <section className="ix-card">
                <div className="ix-card__body" style={{ gap: 'var(--ix-space-sm)' }}>
                  <div className="ix-between">
                    <h2 className="dash-side-title" style={{ fontSize: 16 }}>
                      Active Briefings
                    </h2>
                    <Link className="ix-link-action" to="/briefings" style={{ fontSize: 12 }}>
                      + New
                    </Link>
                  </div>
                  <div className="dash-briefs">
                    {data.briefings.map((brief) => (
                      <div className="dash-brief" key={brief.id}>
                        <span className="dash-brief__tile">
                          <Icon name={icon(brief.icon)} size={16} />
                        </span>
                        <div className="dash-brief__text">
                          <div className="dash-brief__name">{brief.name}</div>
                          <div className="dash-brief__sub">{brief.frequencyLabel}</div>
                        </div>
                        <span className="dash-dot" />
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {data.portfolio && (
                <section className="ix-card">
                  <div className="ix-card__body">
                    <h2 className="dash-side-title">{data.portfolio.sourceLabel}</h2>
                    {isPublic ? (
                      <HiddenInPublicView what="Your premium and policy counts" />
                    ) : (
                    <>
                    <div className="dash-kv">
                      <div>
                        <div className="dash-kv-label">Premium</div>
                        <div className="dash-kv-value" style={{ color: 'var(--ix-primary)' }}>
                          {data.portfolio.premiumDisplay}
                        </div>
                      </div>
                      <div>
                        <div className="dash-kv-label">Policies</div>
                        <div className="dash-kv-value" style={{ color: 'var(--ix-text-primary)' }}>
                          {data.portfolio.hasData
                            ? data.portfolio.policyCount.toLocaleString('en-US')
                            : '—'}
                        </div>
                      </div>
                    </div>
                    {data.portfolio.hasData && (
                    <div
                      style={{
                        display: 'inline-flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        alignSelf: 'flex-start',
                        gap: 6,
                        padding: '7px 10px',
                        borderRadius: 'var(--ix-radius-md)',
                        background: 'var(--ix-success-tint)',
                        fontSize: 11,
                        fontWeight: 500,
                        lineHeight: '100%',
                        color: 'var(--ix-success)',
                      }}
                    >
                      <Icon name="check" size={12} />
                      <span>{data.portfolio.badge}</span>
                    </div>
                    )}
                    <p
                      style={{
                        fontSize: 11,
                        fontWeight: 400,
                        lineHeight: '150%',
                        color: 'var(--ix-text-secondary)',
                        textWrap: 'pretty',
                      }}
                    >
                      {data.portfolio.note}
                    </p>
                    </>
                    )}
                  </div>
                </section>
              )}

              <section className="ix-card">
                <div className="ix-card__body" style={{ gap: 'var(--ix-space-sm)' }}>
                  <div className="ix-between">
                    <h2 className="dash-side-title" style={{ fontSize: 16 }}>
                      Connections
                    </h2>
                    <Link className="ix-link-action" to="/connections" style={{ fontSize: 12 }}>
                      Manage →
                    </Link>
                  </div>
                  <div className="dash-briefs dash-conn">
                    {/* Only verified connections appear here, so an empty panel
                        is a real state rather than a loading one. */}
                    {data.connections.length === 0 && (
                      <p className="dash-conn__empty">
                        No delivery channel is connected yet.{' '}
                        <Link to="/connections">Connect one</Link> to start receiving alerts
                        outside Insuronix.
                      </p>
                    )}
                    {data.connections.map((connection) => (
                      <div className="dash-brief" key={connection.id}>
                        <span className="dash-brief__tile">
                          {connection.kind && CHANNEL_LOGOS[connection.kind] ? (
                            <img src={CHANNEL_LOGOS[connection.kind]} alt="" width={18} height={18} />
                          ) : connection.kind && CONNECTION_LOGOS[connection.kind] ? (
                            <BrandLogo name={CONNECTION_LOGOS[connection.kind]} size={18} />
                          ) : (
                            <Icon name={icon(connection.icon)} size={16} />
                          )}
                        </span>
                        <div className="dash-brief__text">
                          <div className="dash-brief__name">{connection.name}</div>
                        </div>
                        <span className="dash-dot" />
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            </div>
          </div>
        </>
      )}
    </AppLayout>
  )
}

const STATUS_FILTERS = [
  // Every filter names its own dimension in its "all" option: three unlabelled
  // dropdowns in a row, one of them reading only "All", reads as a filter that
  // has gone missing rather than one that is not narrowing anything.
  { value: 'all', label: 'Any status' },
  { value: 'todo', label: 'Needs action' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'deferred', label: 'Deferred' },
  { value: 'ticket', label: 'Has ticket' },
] as const

/**
 * The dashboard's circulars, with the filters a front page needs: find one by
 * text, narrow to a state, or see only what nobody has dealt with yet. Filtering
 * happens here on what the dashboard already returned - no extra requests.
 */
function RecentCirculars({ circulars }: { circulars: CircularDetail[] }) {
  const [query, setQuery] = useState('')
  const [stateCode, setStateCode] = useState('all')
  const [status, setStatus] = useState<string>('all')
  // Mandatory or needs review: the editor's badge where one was written, the
  // derived one otherwise - the same tag the card shows.
  const [badge, setBadge] = useState<string>('all')
  const viewer = useViewerState()

  const states = Array.from(new Set(circulars.map((c) => c.stateCode))).sort()
  const needle = query.trim().toLowerCase()

  const shown = circulars.filter((c) => {
    if (stateCode !== 'all' && c.stateCode !== stateCode) return false
    if (badge !== 'all' && (c.analysis?.badge ?? c.derivedBadge) !== badge) return false
    if (needle) {
      const haystack = `${c.code} ${c.subject} ${c.note} ${c.bureauLabel ?? ''}`.toLowerCase()
      if (!haystack.includes(needle)) return false
    }
    if (status === 'all') return true
    const state = viewer.data?.items?.[c.id]
    if (status === 'acknowledged') return !!state?.actions?.acknowledge
    if (status === 'deferred') return !!state?.actions?.defer && !state?.actions?.acknowledge
    if (status === 'ticket') return !!state?.changeResponseId
    // Needs action: nobody has acknowledged it or opened a ticket for it.
    return !state?.actions?.acknowledge && !state?.changeResponseId
  })

  const filtering = needle !== '' || stateCode !== 'all' || status !== 'all' || badge !== 'all'

  return (
    <>
      <div className="dash-filters">
        <label className="dash-filters__search">
          <Icon name="search" size={15} />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search code, subject or note"
            aria-label="Search circulars"
          />
        </label>
        <select value={stateCode} onChange={(e) => setStateCode(e.target.value)} aria-label="Filter by state">
          <option value="all">All states</option>
          {states.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select value={badge} onChange={(e) => setBadge(e.target.value)} aria-label="Filter by whether it is mandatory">
          <option value="all">Any requirement</option>
          <option value="mandatory">Mandatory only</option>
          <option value="review">Needs review only</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by what you have done">
          {STATUS_FILTERS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        {filtering && (
          <button
            className="ix-btn ix-btn--ghost ix-btn--compact"
            type="button"
            onClick={() => {
              setQuery('')
              setStateCode('all')
              setStatus('all')
              setBadge('all')
            }}
          >
            Clear
          </button>
        )}
        <span className="dash-filters__count">
          {shown.length} of {circulars.length}
        </span>
      </div>

      {/* One open at a time: an expanded card is tall enough that two of them
          turn the list into a scroll. */}
      <OneCircularOpen>
        {/* The row layout, which is what this list is for: a dense scan of what
            has arrived, with the tracking number, the priority and the age that
            the feed's own card leaves out. The Circular Feed keeps `cf-item`,
            where there is room for the note and the actions. */}
        <div className="ix-stack" style={{ gap: 10 }}>
          {shown.map((circular) => (
            <CircularCard circular={circular} key={circular.id} />
          ))}
          {shown.length === 0 && (
            <div className="ix-empty">
              <div className="ix-empty__title">No circulars match those filters</div>
              <div className="ix-empty__body">
                Clear the search, or pick another state or status.
              </div>
            </div>
          )}
        </div>
      </OneCircularOpen>
    </>
  )
}

function DashboardSkeleton() {
  return (
    <>
      <div className="ix-skeleton" style={{ height: 210, borderRadius: 'var(--ix-radius-xl)' }} />
      <section className="ix-grid ix-grid--5">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="ix-skeleton" style={{ height: 118, borderRadius: 'var(--ix-radius-xl)' }} />
        ))}
      </section>
      <div className="dash-cols">
        <div className="ix-stack" style={{ gap: 10 }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="ix-skeleton" style={{ height: 92, borderRadius: 'var(--ix-radius-lg)' }} />
          ))}
        </div>
        <div className="dash-side">
          {[0, 1, 2].map((i) => (
            <div key={i} className="ix-skeleton" style={{ height: 160, borderRadius: 'var(--ix-radius-xl)' }} />
          ))}
        </div>
      </div>
    </>
  )
}
